import { useEffect, useState } from 'react';
import { Outlet, useLocation, useParams } from 'react-router-dom';
import { Sidebar } from '../components/Sidebar';
import { Topbar } from '../components/Topbar';
import { ThemeToggle } from '../components/ThemeToggle';
import { Icon } from '../components/Icons';
import { btnIcon } from '../components/buttons';
import { useAuthStore } from '../auth/store';
import { disablePush, enablePush, getPushState, type PushState } from '../lib/push';

function screenMeta(path: string): { eyebrow: string; title: string } {
  if (path === '/') return { eyebrow: 'Snapmon · Internal', title: 'Dashboard' };
  if (path.startsWith('/creators/')) return { eyebrow: 'Snapmon · Internal', title: 'Creator detail' };
  if (path.startsWith('/creators')) return { eyebrow: 'Snapmon · Internal', title: 'Creators' };
  if (path.startsWith('/codes')) return { eyebrow: 'Snapmon · Internal', title: 'Code requests' };
  if (path.startsWith('/feature-requests')) return { eyebrow: 'Snapmon · Internal', title: 'Feature requests' };
  if (path.startsWith('/payouts')) return { eyebrow: 'Snapmon · Internal', title: 'Payouts' };
  if (path.startsWith('/activity')) return { eyebrow: 'Snapmon · Internal', title: 'Activity' };
  return { eyebrow: 'Snapmon · Internal', title: 'Admin' };
}

/**
 * The topbar bell is the Web Push toggle: tap to subscribe this device
 * to admin notifications (new feature requests, new code applications),
 * tap again to unsubscribe. On iOS it only works once the panel is
 * installed to the home screen — enablePush() explains that on tap.
 */
function PushBellButton() {
  const [state, setState] = useState<PushState>('unsupported');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    getPushState().then(setState).catch(() => {});
  }, []);

  const toggle = async () => {
    if (busy) return;
    setBusy(true);
    try {
      if (state === 'subscribed') {
        await disablePush();
      } else {
        await enablePush();
      }
      setState(await getPushState());
    } catch (err: any) {
      alert(err?.message ?? 'Bildirim ayarı değiştirilemedi.');
    } finally {
      setBusy(false);
    }
  };

  const active = state === 'subscribed';
  return (
    <button
      style={{
        ...btnIcon,
        width: 36,
        height: 36,
        background: active ? 'var(--brand-soft)' : undefined,
        color: active ? 'var(--brand-ink)' : undefined,
        opacity: busy ? 0.5 : 1,
      }}
      title={active ? 'Bildirimler açık — kapatmak için dokun' : 'Bildirimleri aç'}
      onClick={toggle}
    >
      <Icon.Bell />
    </button>
  );
}

export function ShellLayout() {
  const location = useLocation();
  const admin = useAuthStore((s) => s.admin);
  const meta = screenMeta(location.pathname);
  const [menuOpen, setMenuOpen] = useState(false);

  // Close the drawer whenever the route changes (e.g. a nav tap).
  useEffect(() => {
    setMenuOpen(false);
  }, [location.pathname]);

  return (
    <div className="app">
      {/* Desktop: in-grid sidebar (hidden < 901px by CSS). */}
      <Sidebar />

      {/* Mobile: slide-out drawer + backdrop (hidden > 900px by CSS). */}
      <div
        className={`snapmon-drawer-backdrop ${menuOpen ? 'is-open' : ''}`}
        onClick={() => setMenuOpen(false)}
        aria-hidden
      />
      <div className={`snapmon-drawer ${menuOpen ? 'is-open' : ''}`}>
        <Sidebar
          onNavigate={() => setMenuOpen(false)}
          styleOverride={{ position: 'static', height: '100dvh' }}
        />
      </div>

      <main style={{ minWidth: 0, display: 'flex', flexDirection: 'column' }}>
        <Topbar
          eyebrow={meta.eyebrow}
          title={meta.title}
          left={
            <button
              className="snapmon-menu-btn"
              style={{ ...btnIcon, width: 36, height: 36, placeItems: 'center' }}
              title="Menü"
              onClick={() => setMenuOpen(true)}
            >
              <Icon.Menu />
            </button>
          }
          right={
            <>
              <ThemeToggle />
              <PushBellButton />
              {admin && (
                <div
                  style={{
                    width: 36,
                    height: 36,
                    borderRadius: '50%',
                    background: 'var(--brand)',
                    color: 'var(--bg-elev)',
                    display: 'grid',
                    placeItems: 'center',
                    fontFamily: 'var(--font-display)',
                    fontWeight: 700,
                    fontSize: 13,
                    textTransform: 'uppercase',
                  }}
                >
                  {admin.username.slice(0, 2)}
                </div>
              )}
            </>
          }
        />
        <Outlet />
      </main>
    </div>
  );
}
